import { useEffect, useMemo, useState } from 'react';
import { Select, Space, Typography } from 'antd';
import KPICard from '@/components/ui/analytics/KPICard';
import Table from '@/components/ui/table/Table';
import { useTenantDateFormat } from '@hooks/useTenantDateFormat';
import { DashboardContainer, KPICardsGrid, StudentsHeaderLeft, StudentsHeaderRow, StudentsHeaderSub, StudentsHeaderTitle } from '../style';
import { formatMoney, getEntryDisplayDayjs } from './shared';
import { FINANCE_ENTRY_TYPES, subscribeFinanceEntries } from '@services/registry';
import { useAuthStore } from '@stores/auth.store';
import { useFinanceAccounts } from '@/hooks/tenant/finance/useFinanceAccounts';
import { useFinanceSummary } from '@/hooks/tenant/finance/useFinanceSummary';

const { Text } = Typography;

export default function TransactionLedger() {
    const { dateFormat, dateInputFormats } = useTenantDateFormat();
    const [searchText, setSearchText] = useState('');
    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const { rows: accounts } = useFinanceAccounts({ tenantId });
    const { summary } = useFinanceSummary({ tenantId });
    const [entries, setEntries] = useState([]);
    const [accountFilter, setAccountFilter] = useState(undefined);

    useEffect(() => {
        if (!tenantId) {
            setEntries([]);
            return;
        }
        const unsub = subscribeFinanceEntries({
            tenantId,
            accountId: accountFilter,
            onNext: (next) => setEntries(next),
            onError: (err) => console.error(err),
        });
        return () => unsub?.();
    }, [tenantId, accountFilter]);

    const normalized = useMemo(() => {
        const nameById = new Map((accounts || []).map((a) => [a.id, a.name || a.id]));

        const startingBalance = accountFilter
            ? Number((accounts || []).find((a) => a.id === accountFilter)?.openingBalance || 0) || 0
            : (accounts || []).reduce((s, a) => s + (Number(a.openingBalance || 0) || 0), 0);

        const showOpeningRow =
            accountFilter != null ||
            Math.abs(startingBalance) > 0 ||
            (entries || []).length > 0;

        // General journal: show by createdAt (then fallback to date).
        const getSortTs = (r) => {
            const created = getEntryDisplayDayjs(r);
            if (created) return created.valueOf();
            return 0;
        };

        const sorted = [...(entries || [])].sort((a, b) => getSortTs(a) - getSortTs(b));

        let running = startingBalance;
        const chronoRows = [];

        if (showOpeningRow) {
            chronoRows.push({
                id: '__opening__',
                key: '__opening__',
                isOpeningBalanceRow: true,
                accountId: accountFilter,
                accountName: accountFilter
                    ? nameById.get(accountFilter) || '—'
                    : 'All accounts',
                particularsDisplay: 'Opening balance',
                receive: 0,
                expense: 0,
                balance: running,
                type: null,
            });
        }

        for (const r of sorted) {
            const amount = Number(r.amount || 0) || 0;
            const receive = r.type === FINANCE_ENTRY_TYPES.RECEIVE ? amount : 0;
            const expense = r.type === FINANCE_ENTRY_TYPES.EXPENSE ? amount : 0;
            running += receive - expense;
            chronoRows.push({
                ...r,
                key: r.id,
                receive,
                expense,
                balance: running,
                accountName: nameById.get(r.accountId) || '—',
                particularsDisplay: r.particulars || '—',
            });
        }

        // Newest transactions first, but keep opening balance pinned at the top.
        if (!chronoRows.length) return [];
        const [opening, ...restChrono] = chronoRows[0]?.isOpeningBalanceRow
            ? [chronoRows[0], chronoRows.slice(1)]
            : [null, chronoRows];
        const restNewestFirst = [...restChrono].reverse();
        return opening ? [opening, ...restNewestFirst] : restNewestFirst;
    }, [entries, accounts, accountFilter]);

    const filteredRows = useMemo(() => {
        const q = (searchText || '').trim().toLowerCase();
        if (!q) return normalized;
        return normalized.filter((r) => {
            if (r.isOpeningBalanceRow) {
                const blob = `${r.particularsDisplay || ''} ${r.accountName || ''} opening`.toLowerCase();
                return blob.includes(q);
            }
            const particulars = (r.particularsDisplay || r.particulars || '').toLowerCase();
            return particulars.includes(q);
        });
    }, [normalized, searchText]);

    const totals = useMemo(() => {
        if (!tenantId) return { totalReceive: 0, totalExpense: 0, balance: 0, openingBalance: 0 };
        if (accountFilter) {
            const acc = (accounts || []).find((a) => a.id === accountFilter) || null;
            const totalReceive = Number(acc?.totalReceive || 0) || 0;
            const totalExpense = Number(acc?.totalExpense || 0) || 0;
            const balance = Number(acc?.balance || (totalReceive - totalExpense)) || 0;
            const openingBalance = Number(acc?.openingBalance || 0) || 0;
            return { totalReceive, totalExpense, balance, openingBalance };
        }
        const totalReceive = Number(summary?.totalReceive || 0) || 0;
        const totalExpense = Number(summary?.totalExpense || 0) || 0;
        const balance = Number(summary?.balance || (totalReceive - totalExpense)) || 0;
        const openingBalance = (accounts || []).reduce((s, a) => s + (Number(a.openingBalance || 0) || 0), 0);
        return { totalReceive, totalExpense, balance, openingBalance };
    }, [tenantId, accountFilter, accounts, summary]);

    const columns = useMemo(() => ([
        {
            title: 'Date',
            key: 'date',
            render: (_, row) => {
                if (row.isOpeningBalanceRow) return '—';
                const d = getEntryDisplayDayjs(row);
                return d ? d.format(dateFormat) : '—';
            },
        },
        {
            title: 'Account',
            dataIndex: 'accountName',
            key: 'account',
            render: (v) => v || '—',
        },
        {
            title: 'Particulars',
            dataIndex: 'particularsDisplay',
            key: 'particulars',
            render: (v) => v || '—',
        },
        {
            title: 'Receive',
            key: 'receive',
            render: (_, row) => (
                <Text style={{ color: row.receive > 0 ? '#16a34a' : undefined }}>
                    {row.receive ? formatMoney(row.receive) : '—'}
                </Text>
            ),
        },
        {
            title: 'Expense',
            key: 'expense',
            render: (_, row) => (
                <Text style={{ color: row.expense > 0 ? '#ef4444' : undefined }}>
                    {row.expense ? formatMoney(row.expense) : '—'}
                </Text>
            ),
        },
        {
            title: 'Balance',
            key: 'balance',
            render: (_, row) => (
                <Text strong>{Number.isFinite(row.balance) ? formatMoney(row.balance) : '—'}</Text>
            ),
        },
    ]), [dateFormat]);

    return (
        <DashboardContainer>
            <StudentsHeaderRow>
                <StudentsHeaderLeft>
                    <StudentsHeaderTitle>Transaction Ledger</StudentsHeaderTitle>
                    <StudentsHeaderSub>Track receive, expense, and running balance.</StudentsHeaderSub>
                </StudentsHeaderLeft>
                <Space size={10} wrap>
                    <Select
                        value={accountFilter}
                        onChange={(v) => setAccountFilter(v || undefined)}
                        placeholder="All accounts"
                        allowClear
                        style={{ minWidth: 220 }}
                        options={(accounts || []).map((a) => ({
                            value: a.id,
                            label: `${a.name}${a.type ? ` (${a.type})` : ''}`,
                        }))}
                    />
                    <Text type="secondary">Date format: {dateInputFormats?.[0] || dateFormat}</Text>
                </Space>
            </StudentsHeaderRow>

            <KPICardsGrid $columns={4} $rows={1} $height="auto" style={{ marginBottom: 16 }}>
                <KPICard title="Opening balance" value={formatMoney(totals.openingBalance)} />
                <KPICard title="Total Receive" value={formatMoney(totals.totalReceive)} />
                <KPICard title="Total Expense" value={formatMoney(totals.totalExpense)} />
                <KPICard title="Balance" value={formatMoney(totals.balance)} />
            </KPICardsGrid>

            <Table
                title="Ledger"
                columns={columns}
                dataSource={filteredRows}
                loading={false}
                emptyText="No transactions yet"
                searchValue={searchText}
                onSearchChange={setSearchText}
                pagination={true}
                pageSize={10}
                showSearch={true}
                showPagination={true}
            />
        </DashboardContainer>
    );
}

